/**
 * RuleBlockModal - User-friendly modal for rule violations
 * Phase D: Rule Engine
 * 
 * UX: Yellow warning (NOT red error), checklist, progress indicator, override
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { AlertTriangle, CheckCircle2, XCircle, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useRuleOverride } from './useRuleOverrides';
import { useAuth } from '@/hooks/useAuth';
import type { RuleResult, RuleActionContext } from './useRuleEngine';

interface RuleBlockModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOverride?: () => void;
  results: RuleResult[];
  actionContext: RuleActionContext;
  projectId: string;
}

const RuleBlockModal: React.FC<RuleBlockModalProps> = ({
  isOpen,
  onClose,
  onOverride,
  results,
  actionContext,
  projectId,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const overrideMutation = useRuleOverride();
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');

  const passed = results.filter(r => r.passed);
  const violations = results.filter(r => !r.passed);
  const total = results.length;
  const passedCount = passed.length;
  const progressPercent = total > 0 ? (passedCount / total) * 100 : 0;

  const actionLabel = getActionLabel(actionContext.action);
  const entityLabel = actionContext.entityLabel || actionContext.zone || '';

  const handleOverride = async () => {
    if (!user?.id || overrideReason.length < 20) return;

    // Override each blocking violation
    for (const violation of violations.filter(v => v.severity === 'block')) {
      await overrideMutation.mutateAsync({
        projectId,
        ruleCode: violation.ruleCode,
        reason: overrideReason,
        context: {
          action: actionContext.action,
          targetModule: actionContext.targetModule,
          zone: actionContext.zone,
          entityId: actionContext.entityId,
          entityLabel: actionContext.entityLabel,
        },
        userId: user.id,
        module: actionContext.targetModule,
        entityLabel: `Override ${violation.ruleCode} - ${entityLabel}`,
      });
    }

    setShowOverrideForm(false);
    setOverrideReason('');
    onOverride?.();
    onClose();
  };

  const handleNavigate = (path: string) => {
    onClose();
    navigate(path);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-5 w-5" />
            Chưa đủ điều kiện để tiếp tục
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Action description */}
          <p className="text-sm text-muted-foreground">
            Để <strong>{actionLabel}</strong> cho <strong>{entityLabel}</strong>, bạn cần:
          </p>

          {/* Checklist */}
          <div className="space-y-2">
            {/* Passed rules first */}
            {passed.map((r) => (
              <div key={r.ruleCode} className="flex items-start gap-2 p-2 rounded-md bg-green-50 dark:bg-green-950/30">
                <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <span className="text-green-700 dark:text-green-400">{r.message}</span>
                  <span className="text-muted-foreground ml-1">(đã xong)</span>
                </div>
              </div>
            ))}

            {/* Violations */}
            {violations.map((r) => (
              <div key={r.ruleCode} className="flex items-start gap-2 p-2 rounded-md bg-red-50 dark:bg-red-950/30">
                <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                <div className="text-sm flex-1">
                  <span className="text-red-700 dark:text-red-400">{r.message}</span>
                  {r.linkPath && (
                    <button
                      onClick={() => handleNavigate(r.linkPath!)}
                      className="flex items-center gap-1 text-xs text-primary hover:underline mt-1"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Đi đến {getModuleLabel(r.sourceModule)}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Progress indicator */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Tiến độ: {passedCount}/{total} điều kiện</span>
              <span>{Math.round(progressPercent)}%</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>

          {/* Override form */}
          {showOverrideForm && (
            <div className="space-y-2 border-t pt-3">
              <label className="text-sm font-medium">Lý do override:</label>
              <Textarea
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Nhập lý do override (tối thiểu 20 ký tự)..."
                rows={3}
              />
              <div className="flex justify-between items-center">
                <span className={`text-xs ${overrideReason.length < 20 ? 'text-red-500' : 'text-muted-foreground'}`}>
                  {overrideReason.length}/20 ký tự tối thiểu
                </span>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleOverride}
                  disabled={overrideReason.length < 20 || overrideMutation.isPending}
                >
                  {overrideMutation.isPending ? 'Đang xử lý...' : 'Xác nhận Override'}
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Hủy
          </Button>
          {!showOverrideForm && violations.some(v => v.severity === 'block') && (
            <Button
              variant="secondary"
              onClick={() => setShowOverrideForm(true)}
              className="text-amber-700 dark:text-amber-400"
            >
              Override (PM only)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function getActionLabel(action: string): string {
  const labels: Record<string, string> = {
    create_daily_plan: 'tạo Daily Plan',
    create_po: 'tạo PO',
    mark_paid: 'chuyển thanh toán',
    milestone_100: 'chuyển milestone sang 100%',
  };
  return labels[action] || action;
}

function getModuleLabel(module: string): string {
  const labels: Record<string, string> = {
    drawings: 'Drawings',
    specs: 'Specs',
    permit: 'Permit',
    closing: 'Closing',
    plan: 'Plan',
    procurement: 'Procurement',
  };
  return labels[module] || module;
}

export default RuleBlockModal;
